import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import VisitorsCard from '../../admin/VisitorsCard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// ─── Interfaces ──────────────────────────────────────────────────────────────
// S3 interfaces moved to S3Settings.tsx

interface StorageStats {
  totalFiles: number;
  totalSize: number;
  totalSizeFormatted: string;
  fileTypes: Record<string, number>;
  diskSpace: {
    total: number; free: number; used: number;
    totalFormatted: string; freeFormatted: string; usedFormatted: string;
  };
  uploadsFolderPercentage: number;
}

interface ServerStats {
  cpu: { count: number; model: string; usage: string; perCore: string[] };
  memory: {
    total: number; free: number; used: number; usagePercent: string;
    totalFormatted: string; freeFormatted: string; usedFormatted: string;
  };
  process: {
    heapUsed: number; heapTotal: number; rss: number;
    heapUsedFormatted: string; heapTotalFormatted: string; rssFormatted: string;
  };
  system: { platform: string; arch: string; hostname: string; uptime: { days: number; hours: number; minutes: number; formatted: string } };
  network?: { totalRx: number; totalTx: number; totalRxFormatted: string; totalTxFormatted: string; rxPerSec: number; txPerSec: number; rxPerSecFormatted: string; txPerSecFormatted: string };
  diskIO?: { totalReads: number; totalWrites: number; totalReadBytes: number; totalWriteBytes: number; totalReadFormatted: string; totalWriteFormatted: string; readBytesPerSec: number; writeBytesPerSec: number; readPerSecFormatted: string; writePerSecFormatted: string };
  topProcesses?: { pid: number; user: string; cpu: number; mem: number; command: string }[];
  requestsPerMinute?: number;
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Speedometer — arc from 7-o'clock to 5-o'clock (240 deg sweep)
   All positions computed via trig — no CSS transforms, no transform-origin.
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const ARC_DEG = 240;
const START_DEG = 150; // 7-o'clock

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arc(cx: number, cy: number, r: number, s: number, e: number) {
  const sp = polar(cx, cy, r, s);
  const ep = polar(cx, cy, r, e);
  return `M ${sp.x} ${sp.y} A ${r} ${r} 0 ${e - s > 180 ? 1 : 0} 1 ${ep.x} ${ep.y}`;
}

// Needle polygon points computed from angle — no rotation needed
function needlePoints(cx: number, cy: number, angleDeg: number, length: number, halfWidth: number) {
  const rad = (angleDeg * Math.PI) / 180;
  const perpRad = rad + Math.PI / 2;
  // Tip
  const tx = cx + length * Math.cos(rad);
  const ty = cy + length * Math.sin(rad);
  // Base left/right
  const tailLen = 8;
  const bx = cx - tailLen * Math.cos(rad);
  const by = cy - tailLen * Math.sin(rad);
  const lx = bx + halfWidth * Math.cos(perpRad);
  const ly = by + halfWidth * Math.sin(perpRad);
  const rx = bx - halfWidth * Math.cos(perpRad);
  const ry = by - halfWidth * Math.sin(perpRad);
  return `${tx},${ty} ${lx},${ly} ${rx},${ry}`;
}

const ZONE_GREEN = '#22c55e';
const ZONE_YELLOW = '#facc15';
const ZONE_ORANGE = '#f97316';
const ZONE_RED = '#ef4444';

function zoneColor(pct: number) {
  return pct < 60 ? ZONE_GREEN : pct < 80 ? ZONE_ORANGE : ZONE_RED;
}

interface SpeedoProps { pct: number; label: string; sublabel?: string; size?: number; id: string; tickLabels?: string[] }

/**
 * Animated Speedometer with spring-physics needle.
 * Uses requestAnimationFrame to interpolate between old and new angles
 * with damped spring easing — slight overshoot then settle, like a real gauge.
 */
const Speedometer: React.FC<SpeedoProps> = ({ pct, label, sublabel, size = 160, id, tickLabels }) => {
  const clamped = Math.max(0, Math.min(100, pct));
  const targetAngle = START_DEG + (clamped / 100) * ARC_DEG;

  const cx = 100, cy = 100, R = 76;
  const tickPcts = [0, 20, 40, 60, 80, 100];
  // Use custom labels if provided, otherwise show percentage values
  const tickLbls = tickLabels || tickPcts.map(String);

  // ── Spring-physics animation state ──
  const needleRef = useRef<SVGPolygonElement>(null);
  const hubRef = useRef<SVGCircleElement>(null);
  const glowRef = useRef<SVGStopElement>(null);
  const currentAngle = useRef(START_DEG); // where needle is now
  const velocity = useRef(0);             // angular velocity (deg/frame)
  const rafId = useRef(0);
  const targetRef = useRef(targetAngle);  // track latest target in ref
  const frameCount = useRef(0);           // for micro-tremor timing

  // Spring constants — tuned for car-speedometer feel
  const STIFFNESS = 0.06;   // slightly softer for longer sweeps
  const DAMPING = 0.78;     // mild overshoot then settle
  const TREMOR_AMP = 0.35;  // subtle needle vibration (degrees) — like a real gauge

  // Keep target ref in sync
  useEffect(() => {
    targetRef.current = targetAngle;
  }, [targetAngle]);

  // Single continuous animation loop — runs for the lifetime of the component.
  // Chases targetRef.current with spring physics + micro-tremor so
  // needle always looks alive, even when the value barely changes.
  useEffect(() => {
    let running = true;

    const animate = () => {
      if (!running) return;
      frameCount.current++;

      const target = targetRef.current;
      const cur = currentAngle.current;
      const diff = target - cur;

      // Spring force + damping
      velocity.current = velocity.current * DAMPING + diff * STIFFNESS;
      currentAngle.current += velocity.current;

      // Micro-tremor — subtle oscillation that makes the needle look alive
      // (like a real analogue gauge vibrating from engine/electrical noise).
      // Only when nearly settled so it doesn't interfere with big sweeps.
      let displayAngle = currentAngle.current;
      if (Math.abs(diff) < 2 && Math.abs(velocity.current) < 0.5) {
        const tremor = Math.sin(frameCount.current * 0.15) * TREMOR_AMP
                     + Math.sin(frameCount.current * 0.07) * TREMOR_AMP * 0.5;
        displayAngle += tremor;
      }

      // Update SVG elements directly (no React re-render)
      if (needleRef.current) {
        needleRef.current.setAttribute('points', needlePoints(cx, cy, displayAngle, 60, 3));
        // Update needle + hub color based on current animated position
        const animPct = ((currentAngle.current - START_DEG) / ARC_DEG) * 100;
        const col = zoneColor(Math.max(0, Math.min(100, animPct)));
        needleRef.current.setAttribute('fill', col);
        if (hubRef.current) hubRef.current.setAttribute('fill', col);
        if (glowRef.current) glowRef.current.setAttribute('stop-color', col);
      }

      rafId.current = requestAnimationFrame(animate);
    };

    rafId.current = requestAnimationFrame(animate);
    return () => { running = false; cancelAnimationFrame(rafId.current); };
  }, []); // runs once — never restarts

  // Initial needle color (will be overridden by animation)
  const needleColor = zoneColor(clamped);

  return (
    <svg width={size} height={size * 0.78} viewBox="0 0 200 156">
      <defs>
        <radialGradient id={`bz-${id}`} cx="50%" cy="42%" r="52%">
          <stop offset="0%" stopColor="#2d2d3a" />
          <stop offset="100%" stopColor="#18181f" />
        </radialGradient>
        <filter id={`gl-${id}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Needle tip glow */}
        <radialGradient id={`ng-${id}`} cx="50%" cy="50%" r="50%">
          <stop ref={glowRef} offset="0%" stopColor={needleColor} stopOpacity="0.4" />
          <stop offset="100%" stopColor="transparent" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Bezel */}
      <circle cx={cx} cy={cy} r={92} fill={`url(#bz-${id})`} stroke="#3a3a4a" strokeWidth="2.5" />
      <circle cx={cx} cy={cy} r={89} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.8" />

      {/* Zone arcs */}
      <path d={arc(cx, cy, R, START_DEG, START_DEG + 0.6 * ARC_DEG)} fill="none" stroke={ZONE_GREEN} strokeWidth="5" strokeLinecap="round" opacity="0.65" />
      <path d={arc(cx, cy, R, START_DEG + 0.6 * ARC_DEG, START_DEG + 0.75 * ARC_DEG)} fill="none" stroke={ZONE_YELLOW} strokeWidth="5" opacity="0.65" />
      <path d={arc(cx, cy, R, START_DEG + 0.75 * ARC_DEG, START_DEG + 0.85 * ARC_DEG)} fill="none" stroke={ZONE_ORANGE} strokeWidth="5" opacity="0.65" />
      <path d={arc(cx, cy, R, START_DEG + 0.85 * ARC_DEG, START_DEG + ARC_DEG)} fill="none" stroke={ZONE_RED} strokeWidth="5" strokeLinecap="round" opacity="0.75" />

      {/* Ticks */}
      {tickPcts.map((v, ti) => {
        const a = START_DEG + (v / 100) * ARC_DEG;
        const o = polar(cx, cy, R - 7, a);
        const i = polar(cx, cy, R - 18, a);
        const lp = polar(cx, cy, R - 28, a);
        return (
          <g key={v}>
            <line x1={o.x} y1={o.y} x2={i.x} y2={i.y} stroke="rgba(255,255,255,0.75)" strokeWidth="1.8" />
            <text x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="central"
              fontSize={tickLbls[ti].length > 3 ? 6.5 : 8} fill="rgba(255,255,255,0.5)" fontWeight="500" fontFamily="system-ui">{tickLbls[ti]}</text>
          </g>
        );
      })}
      {/* Minor ticks */}
      {tickPcts.slice(0, -1).map((v, idx) => {
        const next = tickPcts[idx + 1];
        const step = (next - v) / 5;
        return Array.from({ length: 4 }, (_, j) => {
          const sv = v + step * (j + 1);
          const a = START_DEG + (sv / 100) * ARC_DEG;
          const o = polar(cx, cy, R - 7, a);
          const i2 = polar(cx, cy, R - 12, a);
          return <line key={`${v}-${j}`} x1={o.x} y1={o.y} x2={i2.x} y2={i2.y} stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" />;
        });
      })}

      {/* Needle — animated via ref, spring physics */}
      <polygon
        ref={needleRef}
        points={needlePoints(cx, cy, currentAngle.current, 60, 3)}
        fill={needleColor}
        filter={`url(#gl-${id})`}
        opacity="0.92"
      />

      {/* Hub */}
      <circle cx={cx} cy={cy} r={8} fill="#222230" stroke="#444" strokeWidth="1.2" />
      <circle ref={hubRef} cx={cx} cy={cy} r={3.5} fill={needleColor} opacity="0.9" />

      {/* Digital value */}
      <text x={cx} y={cy + 28} textAnchor="middle" fontSize={label.length > 6 ? 14 : 18} fontWeight="700" fill="#fff" fontFamily="system-ui">{label}</text>
      {sublabel && (
        <text x={cx} y={cy + 42} textAnchor="middle" fontSize={sublabel.length > 16 ? 7 : 8} fill="rgba(255,255,255,0.45)" fontWeight="500" fontFamily="system-ui"
          style={{ textTransform: 'uppercase' as const, letterSpacing: sublabel.length > 16 ? '0.5px' : '1.5px' }}>{sublabel}</text>
      )}
    </svg>
  );
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Styled Components
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const pulse = keyframes`0%,100%{opacity:1}50%{opacity:0.6}`;

const Container = styled.div`width:100%;padding:0;`;

const TopBar = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;flex-wrap:wrap;gap:8px;`;
const SystemInfo = styled.div`display:flex;align-items:center;gap:10px;flex-wrap:wrap;`;
const SysChip = styled.div`display:inline-flex;align-items:center;gap:5px;padding:4px 10px;background:#f1f5f9;border-radius:6px;font-size:11px;color:#475569;font-weight:500;`;
const LiveDot = styled.span`display:inline-block;width:6px;height:6px;border-radius:50%;background:#22c55e;animation:${pulse} 2s ease-in-out infinite;`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 1100px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const DarkTile = styled.div`
  background: linear-gradient(160deg, #1e1e2e 0%, #16161f 100%);
  border: 1px solid #2a2a3a;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
`;

const Tile = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const TileHeader = styled.div<{ $dark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: ${p => p.$dark ? 'rgba(255,255,255,0.9)' : '#1e293b'};
`;

const TileTitle = styled.div<{ $dark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$dark ? 'rgba(255,255,255,0.9)' : '#1e293b'};
  svg { width: 18px; height: 18px; color: ${p => p.$dark ? '#818cf8' : '#6366f1'}; }
`;

const TileBadge = styled.span<{ $color?: string }>`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  background: ${p => p.$color || '#f1f5f9'};
  color: #fff;
`;

const SpeedoCenter = styled.div`display:flex;justify-content:center;`;

const StatRow = styled.div<{ $dark?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
  border-bottom: 1px solid ${p => p.$dark ? 'rgba(255,255,255,0.06)' : '#f8fafc'};
  &:last-child { border-bottom: none; }
`;

const StatLabel = styled.span<{ $dark?: boolean }>`
  color: ${p => p.$dark ? 'rgba(255,255,255,0.45)' : '#94a3b8'};
  font-size: 11px;
`;

const StatValue = styled.span<{ $dark?: boolean }>`
  font-weight: 600;
  color: ${p => p.$dark ? 'rgba(255,255,255,0.9)' : '#1e293b'};
  font-size: 12px;
`;

const CoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(65px, 1fr));
  gap: 5px;
  margin-top: 6px;
`;

const CoreBar = styled.div<{ $pct: number; $color: string }>`
  background: rgba(255,255,255,0.04);
  border-radius: 6px;
  padding: 5px 6px;
  text-align: center;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 100%;
    height: ${p => p.$pct}%;
    background: ${p => p.$color}20;
    transition: height 0.5s ease;
  }
`;

const CoreLabel = styled.div`font-size:9px;color:rgba(255,255,255,0.35);font-weight:500;position:relative;z-index:1;`;
const CoreValue = styled.div<{ $color: string }>`font-size:12px;font-weight:700;color:${p => p.$color};position:relative;z-index:1;`;

const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const ProgressTrack = styled.div`width:100%;height:6px;background:#f1f5f9;border-radius:3px;overflow:hidden;margin:6px 0;`;
const ProgressFill = styled.div<{ $pct: number; $color: string }>`height:100%;width:${p => p.$pct}%;background:${p => p.$color};border-radius:3px;transition:width 0.5s ease;`;

const FileTypeBadge = styled.div<{ $color: string }>`
  display:inline-flex;align-items:center;gap:4px;padding:3px 8px;
  background:${p => p.$color}12;border-radius:12px;font-size:11px;font-weight:500;color:${p => p.$color};
  span{font-weight:600;}
`;
const FileTypeList = styled.div`display:flex;flex-wrap:wrap;gap:5px;`;

const ProcessTable = styled.div`
  overflow-y:auto;max-height:160px;font-size:11px;
  &::-webkit-scrollbar{width:4px}
  &::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:2px}
  table{width:100%;border-collapse:collapse}
  th{text-align:left;padding:3px 4px;font-weight:600;color:#94a3b8;font-size:10px;border-bottom:1px solid #e2e8f0;position:sticky;top:0;background:#fff;text-transform:uppercase;letter-spacing:0.3px}
  td{padding:4px;border-bottom:1px solid #f8fafc;color:#475569}
`;

const LoadingContainer = styled.div`display:flex;flex-direction:column;align-items:center;justify-content:center;padding:3rem 0;gap:12px;`;

const CollapsibleSection = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
  &:hover { background: #f8fafc; }
`;

const ChevronIcon = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
  transform: rotate(${p => p.$open ? '180deg' : '0deg'});
  svg { width: 18px; height: 18px; color: #94a3b8; }
`;

/** Generate 6 evenly-spaced tick labels for a 0→max range (e.g. "0","6","12","18","24","31") */
function makeTickLabels(maxVal: number, unit: string = ''): string[] {
  return [0, 0.2, 0.4, 0.6, 0.8, 1].map(f => {
    const v = maxVal * f;
    if (v >= 1000) return `${(v / 1000).toFixed(0)}${unit ? 'T' : ''}`;
    if (v >= 100) return `${Math.round(v)}${unit}`;
    if (v >= 10) return `${Math.round(v)}${unit}`;
    return `${v.toFixed(v % 1 === 0 ? 0 : 1)}${unit}`;
  });
}

const COLORS = ['#4c6ef5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
function usageColor(pct: number) { return pct < 60 ? '#22c55e' : pct < 80 ? '#f59e0b' : '#ef4444'; }

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Main Component
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const AdminStorageStats: React.FC = () => {
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visitorsOpen, setVisitorsOpen] = useState(false);

  // S3 Storage Management moved to S3Settings.tsx

  const axiosConfig = useMemo(() => ({ withCredentials: true, headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' } }), []);

  const fetchAllStats = useCallback(async () => {
    try {
      const [storageRes, serverRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/storage/stats`, axiosConfig),
        axios.get(`${API_BASE}/api/admin/server/stats`, axiosConfig)
      ]);
      if (storageRes.data.success) setStats(storageRes.data.stats);
      if (serverRes.data.success) setServerStats(serverRes.data.serverStats);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch statistics');
    } finally { setLoading(false); }
  }, [axiosConfig]);

  useEffect(() => { fetchAllStats(); }, [fetchAllStats]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/server/stats`, axiosConfig);
        if (res.data.success) setServerStats(res.data.serverStats);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [axiosConfig]);

  const cpuPct = serverStats ? parseFloat(serverStats.cpu.usage) : 0;
  const memPct = serverStats ? parseFloat(serverStats.memory.usagePercent) : 0;
  const diskPct = stats ? Math.round((stats.diskSpace.used / stats.diskSpace.total) * 100) : 0;
  // req/min as % of 500 (assumed capacity ceiling)
  const reqPct = Math.min(((serverStats?.requestsPerMinute ?? 0) / 500) * 100, 100);
  const peakCore = serverStats?.cpu.perCore?.reduce((max, v, i) => {
    const pv = parseFloat(v);
    return pv > max.val ? { val: pv, idx: i } : max;
  }, { val: 0, idx: 0 }) || { val: 0, idx: 0 };

  if (loading) {
    return (
      <LoadingContainer>
        <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading server statistics...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </LoadingContainer>
    );
  }

  if (error) {
    return <Container><div style={{ padding: '16px 20px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: '0.9rem' }}>{error}</div></Container>;
  }

  return (
    <Container>
      <TopBar>
        <SystemInfo>
          <SysChip><LiveDot /> Live (5s refresh)</SysChip>
          <SysChip>{serverStats?.system.hostname}</SysChip>
          <SysChip>{serverStats?.system.platform} / {serverStats?.system.arch}</SysChip>
          <SysChip>Uptime: {serverStats?.system.uptime.formatted}</SysChip>
        </SystemInfo>
      </TopBar>

      {/* ── Main 3 Tiles ── */}
      <TileGrid>

        {/* ━━━ CPU — shows peak (hottest) core ━━━ */}
        <DarkTile>
          <TileHeader $dark>
            <TileTitle $dark>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" />
                <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
                <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
                <line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="15" x2="23" y2="15" />
                <line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="15" x2="4" y2="15" />
              </svg>
              CPU
            </TileTitle>
            <TileBadge $color={usageColor(peakCore.val)}>Peak {peakCore.val.toFixed(1)}%</TileBadge>
          </TileHeader>

          {/* Main dial shows the hottest core */}
          <SpeedoCenter>
            <Speedometer pct={peakCore.val} label={`${peakCore.val.toFixed(1)}%`} sublabel={`Core ${peakCore.idx} (Hottest)`} size={180} id="cpu" />
          </SpeedoCenter>

          <StatRow $dark>
            <StatLabel $dark>Model</StatLabel>
            <StatValue $dark style={{ fontSize: '10px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={serverStats?.cpu.model}>
              {serverStats?.cpu.model || 'N/A'}
            </StatValue>
          </StatRow>
          <StatRow $dark>
            <StatLabel $dark>Cores</StatLabel>
            <StatValue $dark>{serverStats?.cpu.count || 0}</StatValue>
          </StatRow>
          <StatRow $dark>
            <StatLabel $dark>Avg Load</StatLabel>
            <StatValue $dark style={{ color: usageColor(cpuPct) }}>{cpuPct.toFixed(1)}%</StatValue>
          </StatRow>

          {/* Per-core bars (compact) */}
          <div style={{ marginTop: 6, fontSize: '9px', color: 'rgba(255,255,255,0.35)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Per-Core</div>
          <CoreGrid>
            {(serverStats?.cpu.perCore || []).map((usage, i) => {
              const v = parseFloat(usage);
              const c = usageColor(v);
              const isPeak = i === peakCore.idx;
              return (
                <CoreBar key={i} $pct={v} $color={c} style={isPeak ? { border: `1px solid ${c}`, boxShadow: `0 0 6px ${c}40` } : undefined}>
                  <CoreLabel>{isPeak ? '\u2605 ' : ''}C{i}</CoreLabel>
                  <CoreValue $color={c}>{v.toFixed(0)}%</CoreValue>
                </CoreBar>
              );
            })}
          </CoreGrid>
        </DarkTile>

        {/* ━━━ RAM — dial shows actual GB values ━━━ */}
        <DarkTile>
          <TileHeader $dark>
            <TileTitle $dark>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2" />
                <line x1="6" y1="10" x2="6" y2="14" /><line x1="10" y1="10" x2="10" y2="14" />
                <line x1="14" y1="10" x2="14" y2="14" /><line x1="18" y1="10" x2="18" y2="14" />
              </svg>
              Memory (RAM)
            </TileTitle>
            <TileBadge $color={usageColor(memPct)}>{memPct.toFixed(1)}%</TileBadge>
          </TileHeader>

          <SpeedoCenter>
            <Speedometer pct={memPct} label={serverStats?.memory.usedFormatted || '0'} sublabel={`of ${serverStats?.memory.totalFormatted || '0'}`} size={180} id="mem"
              tickLabels={serverStats ? makeTickLabels(serverStats.memory.total / (1024 * 1024 * 1024), 'G') : undefined} />
          </SpeedoCenter>

          <StatRow $dark><StatLabel $dark>Total</StatLabel><StatValue $dark>{serverStats?.memory.totalFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Used</StatLabel><StatValue $dark style={{ color: usageColor(memPct) }}>{serverStats?.memory.usedFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Free</StatLabel><StatValue $dark>{serverStats?.memory.freeFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Node.js Heap</StatLabel><StatValue $dark>{serverStats?.process.heapUsedFormatted} / {serverStats?.process.heapTotalFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Node.js RSS</StatLabel><StatValue $dark>{serverStats?.process.rssFormatted}</StatValue></StatRow>
        </DarkTile>

        {/* ━━━ Disk — dial shows actual GB values ━━━ */}
        <DarkTile>
          <TileHeader $dark>
            <TileTitle $dark>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
              Disk
            </TileTitle>
            <TileBadge $color={usageColor(diskPct)}>{diskPct}%</TileBadge>
          </TileHeader>

          <SpeedoCenter>
            <Speedometer pct={diskPct} label={stats?.diskSpace.usedFormatted || '0'} sublabel={`of ${stats?.diskSpace.totalFormatted || '0'}`} size={180} id="disk"
              tickLabels={stats ? makeTickLabels(stats.diskSpace.total / (1024 * 1024 * 1024), 'G') : undefined} />
          </SpeedoCenter>

          <StatRow $dark><StatLabel $dark>Total</StatLabel><StatValue $dark>{stats?.diskSpace.totalFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Used</StatLabel><StatValue $dark style={{ color: usageColor(diskPct) }}>{stats?.diskSpace.usedFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Free</StatLabel><StatValue $dark>{stats?.diskSpace.freeFormatted}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Total Files</StatLabel><StatValue $dark>{stats?.totalFiles.toLocaleString() || 0}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Files Size</StatLabel><StatValue $dark>{stats?.totalSizeFormatted || '0 B'}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Uploads % of Disk</StatLabel><StatValue $dark>{stats?.uploadsFolderPercentage.toFixed(3)}%</StatValue></StatRow>
        </DarkTile>
      </TileGrid>

      {/* ── Bottom Row: Network+IO (1fr) | File Types + Top Processes (2fr) ── */}
      <BottomGrid>
        {/* ━━━ Network + Requests + Disk I/O — with speedometer ━━━ */}
        <DarkTile>
          <TileHeader $dark>
            <TileTitle $dark>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
              Network & I/O
            </TileTitle>
            <TileBadge $color={usageColor(reqPct)}>{serverStats?.requestsPerMinute ?? 0} req/min</TileBadge>
          </TileHeader>

          {/* Speedometer for req/min — 500 req/min = 100% */}
          <SpeedoCenter>
            <Speedometer pct={reqPct} label={`${serverStats?.requestsPerMinute ?? 0}`} sublabel="Requests / Min" size={180} id="net"
              tickLabels={['0', '100', '200', '300', '400', '500']} />
          </SpeedoCenter>

          {/* Network rates */}
          <div style={{ display: 'flex', gap: 16, marginTop: 6, marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <StatLabel $dark>Inbound</StatLabel>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#10b981', marginTop: 2 }}>{serverStats?.network?.rxPerSecFormatted || '0 B/s'}</div>
            </div>
            <div style={{ flex: 1 }}>
              <StatLabel $dark>Outbound</StatLabel>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#3b82f6', marginTop: 2 }}>{serverStats?.network?.txPerSecFormatted || '0 B/s'}</div>
            </div>
          </div>
          <StatRow $dark><StatLabel $dark>Total Received</StatLabel><StatValue $dark>{serverStats?.network?.totalRxFormatted || 'N/A'}</StatValue></StatRow>
          <StatRow $dark><StatLabel $dark>Total Sent</StatLabel><StatValue $dark>{serverStats?.network?.totalTxFormatted || 'N/A'}</StatValue></StatRow>

          {/* Disk I/O */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 6, paddingTop: 6 }}>
            <StatLabel $dark>Disk I/O</StatLabel>
            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              <div style={{ flex: 1 }}><StatLabel $dark>Read</StatLabel><div style={{ fontWeight: 600, color: '#a78bfa', fontSize: '13px' }}>{serverStats?.diskIO?.readPerSecFormatted || '0 B/s'}</div></div>
              <div style={{ flex: 1 }}><StatLabel $dark>Write</StatLabel><div style={{ fontWeight: 600, color: '#fbbf24', fontSize: '13px' }}>{serverStats?.diskIO?.writePerSecFormatted || '0 B/s'}</div></div>
            </div>
          </div>
        </DarkTile>

        {/* ━━━ File Types + Top CPU Processes (wide) ━━━ */}
        <Tile>
          <TileHeader>
            <TileTitle>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" /></svg>
              File Types
            </TileTitle>
          </TileHeader>
          <FileTypeList>
            {stats && Object.entries(stats.fileTypes).sort((a, b) => b[1] - a[1]).slice(0, 16).map(([ext, count], i) => (
              <FileTypeBadge key={ext} $color={COLORS[i % COLORS.length]}>{ext || '?'} <span>{count}</span></FileTypeBadge>
            ))}
          </FileTypeList>
          <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 10 }}>
            <TileTitle style={{ fontSize: '12px', marginBottom: 6 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}><rect x="4" y="4" width="16" height="16" rx="2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
              Top CPU Processes
            </TileTitle>
            <ProcessTable>
              <table>
                <thead><tr><th>PID</th><th style={{ textAlign: 'left' }}>User</th><th style={{ textAlign: 'right' }}>CPU%</th><th style={{ textAlign: 'right' }}>MEM%</th><th>Command</th></tr></thead>
                <tbody>
                  {(serverStats?.topProcesses || []).map((proc, i) => (
                    <tr key={proc.pid} style={{ background: i === 0 ? 'rgba(239,68,68,0.03)' : 'transparent' }}>
                      <td style={{ fontFamily: 'monospace', fontSize: '10px', color: '#64748b' }}>{proc.pid}</td>
                      <td style={{ fontSize: '10px', color: '#64748b' }}>{proc.user}</td>
                      <td style={{ textAlign: 'right', fontWeight: proc.cpu > 5 ? 600 : 400, color: proc.cpu > 50 ? '#ef4444' : proc.cpu > 10 ? '#f59e0b' : '#1e293b' }}>{proc.cpu.toFixed(1)}</td>
                      <td style={{ textAlign: 'right', color: '#64748b' }}>{proc.mem.toFixed(1)}</td>
                      <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '10px' }} title={proc.command}>{proc.command}</td>
                    </tr>
                  ))}
                  {(!serverStats?.topProcesses || serverStats.topProcesses.length === 0) && (
                    <tr><td colSpan={5} style={{ padding: 8, textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>
                  )}
                </tbody>
              </table>
            </ProcessTable>
          </div>
        </Tile>
      </BottomGrid>

      {/* S3 Storage Management moved to dedicated S3 & Backups page */}
      {/* Live Visitors — collapsible */}
      <CollapsibleSection>
        <CollapsibleHeader onClick={() => setVisitorsOpen(p => !p)}>
          <TileTitle>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
            Live Visitors
          </TileTitle>
          <ChevronIcon $open={visitorsOpen}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </ChevronIcon>
        </CollapsibleHeader>
        {visitorsOpen && <VisitorsCard />}
      </CollapsibleSection>
    </Container>
  );
};

export default AdminStorageStats;
